import { type ChangeEvent, useState } from 'react';
import { usePersistedState } from '../use-persisted-state';
import { Panel } from '../whoop-panel';
import { C } from '../whoop-theme';

interface Experiment {
  id: number;
  question: string;
  metric: string;
  comparison: string;
  range: string;
  deltas: { k: string; v: string }[];
  n: number;
  confidence: string;
  note: string;
}

const SEED: Experiment[] = [
  {
    id: 1,
    question: 'Does alcohol affect my next-day recovery?',
    metric: 'Recovery',
    comparison: 'Days after a drink vs. dry days',
    range: '90 days',
    deltas: [
      { k: 'Recovery', v: '−9%' },
      { k: 'HRV', v: '−11%' },
      { k: 'Sleep performance', v: '−6%' },
    ],
    n: 14,
    confidence: 'Moderate',
    note: 'Observational. Drinking days also skew later and more social, which independently shifts bedtime.',
  },
  {
    id: 2,
    question: 'Morning vs. evening runs — which recovers better?',
    metric: 'Next-day recovery',
    comparison: 'AM sessions vs. PM sessions',
    range: '90 days',
    deltas: [
      { k: 'Next-day recovery', v: '+4%' },
      { k: 'Sleep onset', v: '−12 min' },
      { k: 'HRV', v: '+3%' },
    ],
    n: 31,
    confidence: 'Low–moderate',
    note: 'Small effect. Morning runs slightly favor next-day recovery, mostly via earlier, more consistent bedtimes.',
  },
  {
    id: 3,
    question: 'Does a consistent bedtime improve HRV?',
    metric: 'HRV',
    comparison: 'Bedtime within ±20 min of average vs. outside',
    range: '60 days',
    deltas: [
      { k: 'HRV', v: '+8%' },
      { k: 'Recovery', v: '+6%' },
      { k: 'Resting HR', v: '−2 bpm' },
    ],
    n: 22,
    confidence: 'Moderate',
    note: 'The clearest pattern in your data — regular bedtimes track with higher HRV.',
  },
];

const FILTER_DEFS: [string, string][] = [
  ['travel', 'Travel days'],
  ['alcohol', 'Alcohol days'],
  ['illness', 'Illness days'],
  ['highStrain', 'Very high strain'],
];

const METRIC_OPTS = ['Next-day recovery', 'Sleep performance', 'HRV', 'Resting HR', 'Strain'];
const RANGE_OPTS = ['30 days', '60 days', '90 days'];

const DEFAULT_FORM = { question: '', metric: 'Next-day recovery', range: '90 days' };

export function ExperimentsPage() {
  const [experiments, setExperiments] = usePersistedState<Experiment[]>('experiments', SEED);
  const [filters, setFilters] = usePersistedState<Record<string, boolean>>('expFilters', {
    travel: false,
    alcohol: false,
    illness: false,
    highStrain: false,
  });
  const [form, setForm] = useState(DEFAULT_FORM);
  const canAdd = form.question.trim().length > 4;

  const toggleFilter = (key: string) => setFilters({ ...filters, [key]: !filters[key] });

  const addExperiment = () => {
    if (!canAdd) return;
    const next: Experiment = {
      id: Date.now(),
      question: form.question.trim(),
      metric: form.metric,
      comparison: 'Auto-grouped from journal tags',
      range: form.range,
      deltas: [
        { k: form.metric, v: 'pending' },
        { k: 'Sample', v: 'collecting' },
      ],
      n: 0,
      confidence: 'Low — collecting',
      note: 'Newly created. The system will surface a pattern once enough tagged days accumulate.',
    };
    setExperiments([next, ...experiments]);
    setForm(DEFAULT_FORM);
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.025em', margin: 0 }}>
          Experiments
        </h1>
        <div style={{ fontSize: 14, color: C.text2, marginTop: 4 }}>
          A personal lab — which habits seem to move recovery, sleep and performance. Patterns, not
          proof.
        </div>
      </div>

      {/* Create */}
      <Panel style={{ padding: '22px 24px', marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>New experiment</div>
        <FormLabel>QUESTION</FormLabel>
        <input
          type="text"
          value={form.question}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setForm({ ...form, question: e.target.value })
          }
          placeholder="e.g. Does late caffeine affect my sleep?"
          className="ww-input"
          style={{
            width: '100%',
            fontFamily: 'inherit',
            fontSize: 14,
            color: C.text,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 10,
            padding: '12px 14px',
            marginBottom: 18,
            outline: 'none',
          }}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 18 }}>
          <div>
            <FormLabel>METRIC</FormLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {METRIC_OPTS.map((m) => (
                <PillButton
                  key={m}
                  label={m}
                  active={m === form.metric}
                  onClick={() => setForm({ ...form, metric: m })}
                  padX={11}
                />
              ))}
            </div>
          </div>
          <div>
            <FormLabel>TIME RANGE</FormLabel>
            <div style={{ display: 'flex', gap: 6 }}>
              {RANGE_OPTS.map((r) => (
                <PillButton
                  key={r}
                  label={r}
                  active={r === form.range}
                  onClick={() => setForm({ ...form, range: r })}
                  padX={13}
                />
              ))}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={addExperiment}
          style={{
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            borderRadius: 9,
            padding: '11px 18px',
            border: 'none',
            background: canAdd ? C.accent : 'rgba(255,255,255,0.06)',
            color: canAdd ? '#1a1330' : C.text3,
          }}
        >
          + Create experiment
        </button>
      </Panel>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 18,
        }}
      >
        <span style={{ fontSize: 12, color: C.text3, fontFamily: "'JetBrains Mono',monospace" }}>
          EXCLUDE:
        </span>
        {FILTER_DEFS.map(([key, label]) => {
          const active = Boolean(filters[key]);
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggleFilter(key)}
              style={{
                fontFamily: 'inherit',
                fontSize: '12.5px',
                fontWeight: 600,
                cursor: 'pointer',
                border: `1px solid ${active ? 'rgba(224,137,122,0.4)' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: 999,
                padding: '6px 13px',
                background: active ? 'rgba(224,137,122,0.16)' : 'rgba(255,255,255,0.04)',
                color: active ? '#e0897a' : C.text2,
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {experiments.map((ex) => {
          const confColor = ex.confidence.indexOf('Low') === 0 ? C.warn : C.good;
          return (
            <Panel key={ex.id} style={{ padding: '22px 24px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 14,
                  marginBottom: 14,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      letterSpacing: '-0.01em',
                      lineHeight: 1.25,
                    }}
                  >
                    {ex.question}
                  </div>
                  <div style={{ fontSize: '12.5px', color: C.text2, marginTop: 5 }}>
                    {ex.comparison} · {ex.range}
                  </div>
                </div>
                <span
                  style={{
                    flex: 'none',
                    fontSize: '10.5px',
                    fontFamily: "'JetBrains Mono',monospace",
                    color: confColor,
                    border: `1px solid ${confColor}`,
                    borderRadius: 6,
                    padding: '3px 8px',
                    opacity: 0.9,
                  }}
                >
                  {ex.confidence}
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                {ex.deltas.map((dl) => (
                  <StatBox key={dl.k} value={dl.v} label={dl.k} minWidth={120} />
                ))}
                <StatBox value={String(ex.n)} label="days sampled" minWidth={90} />
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 9,
                  alignItems: 'flex-start',
                  fontSize: '12.5px',
                  color: C.text3,
                  lineHeight: 1.5,
                  paddingTop: 13,
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <span style={{ color: C.warn, fontWeight: 700 }}>ⓘ</span>
                {ex.note}
              </div>
            </Panel>
          );
        })}
      </div>
      <div
        style={{
          fontSize: '12.5px',
          color: C.text3,
          lineHeight: 1.5,
          marginTop: 18,
          fontStyle: 'italic',
        }}
      >
        The lab only ever says “this pattern may be worth testing” — never “this caused that”.
        Correlations here are observational and can be shaped by stress, travel, or sleep timing.
      </div>
    </div>
  );
}

function FormLabel({ children }: { children: string }) {
  return (
    <div
      style={{
        fontSize: 11,
        color: C.text3,
        marginBottom: 8,
        fontFamily: "'JetBrains Mono',monospace",
        letterSpacing: '0.08em',
      }}
    >
      {children}
    </div>
  );
}

function PillButton({
  label,
  active,
  onClick,
  padX,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  padX: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: 'inherit',
        fontSize: 12,
        fontWeight: active ? 700 : 500,
        cursor: 'pointer',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 7,
        padding: `7px ${padX}px`,
        background: active ? C.accent : 'transparent',
        color: active ? '#1a1330' : C.text2,
      }}
    >
      {label}
    </button>
  );
}

function StatBox({ value, label, minWidth }: { value: string; label: string; minWidth: number }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 10,
        padding: '11px 15px',
        minWidth,
        background: 'rgba(255,255,255,0.02)',
      }}
    >
      <span style={{ fontSize: 18, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace" }}>
        {value}
      </span>
      <span style={{ fontSize: '11.5px', color: C.text2 }}>{label}</span>
    </div>
  );
}
